<script setup>
import { rejectReasons } from "~/constants";

const {
  applicationId,
  isNew = false,
  isListing = true,
  canDropout = false,
  canDelete = false,
} = defineProps({
  applicationId: String,
  isNew: {
    type: Boolean,
    default() {
      return false;
    },
  },
  isListing: {
    type: Boolean,
    default() {
      return true;
    },
  },
  canDropout: {
    type: Boolean,
    default() {
      return false;
    },
  },
  canDelete: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const confirmCancelVisible = ref(false);
const drop_out_reason = ref("");
const student_agent_reason = ref("");
const menu = ref();

const toast = useToast();
const emits = defineEmits(["close"]);

const handleCancelApplication = async () => {
  await useApiFetch(`student-applications/${applicationId}/booking/denied`, {
    method: "POST",
    body: {
      student_agent_reason: student_agent_reason.value,
    },
    onResponse() {
      toast.add({
        severity: "success",
        summary: "Booking Cancelled",
        detail: "Your booking has been cancelled",
        life: 3000,
      });
      refreshNuxtData("my-applications");
      refreshNuxtData("booked-applications");
      refreshNuxtData("profile-overview");
      refreshNuxtData("booked-applications");
    },
  });
  emits("close");
  confirmCancelVisible.value = false;
};

const cancelConfirm = (event) => {
  confirmCancelVisible.value = true;
};

const confirmDeleteVisible = ref(false);

const dropoutConfirm = () => {
  confirmDeleteVisible.value = true;
};

const items = ref([
  {
    label: "Withdraw Application",
    icon: "pi pi-minus-circle",
    command: dropoutConfirm,
    visible: canDropout,
  },
  {
    label: "Delete Booking",
    icon: "pi pi-minus-circle",
    command: cancelConfirm,
    visible: canDelete,
  },
]);

const toggle = (event) => {
  menu.value.toggle(event);
};

const dropoutApplication = () => {
  useApiFetch(`student-applications/${applicationId}/student/dropout`, {
    method: "PATCH",
    body: {
      drop_out_reason: drop_out_reason.value,
    },
    onResponse() {
      confirmDeleteVisible.value = false;
      emits("close");
      refreshNuxtData("student-application-detail");
      refreshNuxtData("all-applications");
      refreshNuxtData("new-applications");
      refreshNuxtData("overdue-applications");
      refreshNuxtData("student-applications");
      refreshNuxtData("ongoing-applications");
    },
  });
};

onMounted(() => {
  setTimeout(() => {
    if (isNew || !canDropout) {
      items.value.shift();
    }
  }, 300);
});
</script>

<template>
  <Button
    type="button"
    :icon="isListing ? 'pi pi-ellipsis-v' : 'pi pi-ellipsis-h'"
    @click.stop="toggle"
    aria-haspopup="true"
    aria-controls="overlay_menu"
    severity="secondary"
    text
  />
  <Menu
    ref="menu"
    id="overlay_menu"
    :model="items"
    :popup="true"
    :pt="{
      itemIcon: 'text-rose-500 mr-2',
      itemLabel: 'text-rose-500',
    }"
  />

  <Dialog
    v-model:visible="confirmCancelVisible"
    modal
    header="Confirm booking cancel"
    :closable="false"
    :style="{ width: '35rem' }"
  >
    <div class="space-y-3">
      <p class="text-sm tex-muted mb-3 px-1">
        Do you want to cancel this booking?
      </p>
      <Textarea
        v-model="student_agent_reason"
        :rows="4"
        class="w-full"
        placeholder="Add a reason"
      >
      </Textarea>
      <div class="flex justify-end gap-2">
        <Button
          outlined
          label="Keep this booking"
          @click="confirmCancelVisible = false"
        ></Button>
        <Button
          severity="danger"
          label="Booking cancel"
          @click="handleCancelApplication"
          :disabled="!student_agent_reason"
        ></Button>
      </div>
    </div>
  </Dialog>

  <Dialog
    v-model:visible="confirmDeleteVisible"
    modal
    header="Withdraw Application"
    :closable="false"
    :style="{ width: '35rem' }"
  >
    <div class="space-y-3">
      <p class="text-sm tex-muted mb-3 px-1">
        Are you sure you want to dropout this student application? Please be
        aware that this action is permanent and cannot be reversed.
      </p>
      <SelectWithOther
        :options="rejectReasons"
        @onChange="(value) => (drop_out_reason = value)"
      />
      <div class="flex justify-end gap-2">
        <Button
          outlined
          label="Cancel"
          @click="confirmDeleteVisible = false"
        ></Button>
        <Button
          severity="danger"
          label="Withdraw Application"
          @click="dropoutApplication"
          :disabled="!drop_out_reason"
        ></Button>
      </div>
    </div>
  </Dialog>
</template>
